/**
 * @file: 查询层板插入位置
 * @author: xiaolong.huang@dmall.com
 */

import * as CubeTree from '../../../cubeTree/index';
import * as UIToolkit from '../../../UIToolkit/index';
import Select from '../../select/index';
import CodiSystem from '../../../codiSystem/index';
import FrontCubeData from '../../../frontCubeData/index';
import Utils from '../../../utils/util';
import Config from '../../../config/index';
import { layerAboveOneInsertInfo } from './layerAboveOneInsertInfo';
import { getBeamLayerInfo } from './beamLayerInfo';

const defaultLayerHeight: PhysicsMeasurement = Config.get({
    moduleName: 'layer',
    key: 'defaultLayerHeight'
});
const defaultModuleHeight: PhysicsMeasurement = Config.get({
    moduleName: 'layer',
    key: 'defaultModuleHeight'
});

function partitionAddInfo(cubeData: FreeObjMap, insertTarget: CubeId, extendParams: FreeObjMap = {}): InsertInfo {
    if (CubeTree.isShelf(insertTarget)) {
        // 如果是空货架，直接插到货架最底部
        if (CubeTree.isEmptyShelf(insertTarget)) {
            return emptyShelfScene(cubeData, insertTarget, extendParams);
        }

        // 层板数目 >= 1
        return layerCountAboveOne(cubeData, insertTarget, extendParams);
    }

    // 插入对象为画布中的层板
    if (CubeTree.isPartition(insertTarget)) {
        return partitionInStage(cubeData, insertTarget);
    }

    // 插入到画布
    let sketchpadId: CubeId = CubeTree.findSketchpad();

    return {
        insertData: cubeData,
        parentId: sketchpadId,
        index: CubeTree.findChildCubeIds(sketchpadId).length,
        targetId: sketchpadId,
        extendParams: {
            isOverflowShelf: Utils.hasKey(extendParams, 'isOverflowShelf') ? extendParams.isOverflowShelf : false
        }
    };
}

// 空货架场景
function emptyShelfScene(cubeData: FreeObjMap, insertTarget: CubeId, extendParams: FreeObjMap): InsertInfo {
    let bottom: PhysicsMeasurement = 0;

    // 新增横杆
    if (CubeTree.isBeamCubeData(cubeData)) {
        let beamInfo: BeamLayerInfo = getBeamLayerInfo(cubeData);

        if (Utils.isExist(extendParams.spaceHeightUsed)) {
            bottom = Utils.math(extendParams.spaceHeightUsed, beamInfo.moduleHeight, '-');
        }
        else {
            bottom = Utils.math(beamInfo.layerHeight, beamInfo.moduleHeight, '-');
        }

        if (bottom < 0) {
            bottom = 0;
        }
    }

    // 普通层板、顶板直接插到最下面即可
    return {
        insertData: cubeData,
        parentId: insertTarget,
        index: 0,
        targetId: insertTarget,
        physicsRelativePosition: {
            left: 0,
            bottom: bottom
        }
    };
}

function layerCountAboveOne(cubeData: FreeObjMap, insertTarget: CubeId, extendParams: FreeObjMap): InsertInfo {
    let insertInfo: InsertInfo = layerAboveOneInsertInfo(cubeData, insertTarget, extendParams);
    let shelfPhysicsCoordinate: PhysicsRelativeCoordinate = CodiSystem.getRelativePhysicsCoordinate(insertTarget);
    let maxBottom: PhysicsMeasurement = Utils.math(shelfPhysicsCoordinate.height, 10, '-');

    // 溢出货架了, 就把target指向下一个货架
    if (insertInfo.physicsRelativePosition.bottom > maxBottom) {
        let nextShelf: CubeId = CubeTree.findNextShelf(insertTarget);
        let removeSelectCubes: Array<CubeId> = CubeTree.findAllChildren(insertTarget);

        // 把之前的选中态干掉
        removeSelectCubes.push(insertTarget);
        Select.removeGroup(removeSelectCubes);

        // 如果下一个货架不存在，就把target指向画布
        if (!nextShelf) {
            return partitionAddInfo(cubeData, CubeTree.findSketchpad(), {
                ...extendParams,
                isOverflowShelf: true
            });
        }

        return partitionAddInfo(cubeData, nextShelf, extendParams);
    }

    return insertInfo;
}

// 画布中的层板
function partitionInStage(cubeData: FreeObjMap, partition: CubeId): InsertInfo {
    let sketchpadId: CubeId = CubeTree.findSketchpad();
    let partitionCoordinate: PhysicsRelativeCoordinate = CodiSystem.getRelativePhysicsCoordinate(partition);

    return {
        insertData: cubeData,
        parentId: sketchpadId,
        index: CubeTree.findChildCubeIds(sketchpadId).length,
        targetId: partition,
        physicsRelativePosition: {
            left: Utils.math(partitionCoordinate.left, partitionCoordinate.width, '+'),
            bottom: partitionCoordinate.bottom
        }
    };
}

export {
    partitionAddInfo
};
